import React, { useState, useEffect } from "react";
import { Pie, Bar, Line } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  ArcElement,
  Legend,
} from "chart.js";
import "./QuoteyDashboard.css"; // Make sure you have the CSS for .filter-bar

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  ArcElement,
  Legend,
  ChartDataLabels
);

const BrokersTab = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedBrokerage, setSelectedBrokerage] = useState("all");
  const [brokerageList, setBrokerageList] = useState([]);

  const [brokersData, setBrokersData] = useState({
    brokers: [],
    stats: {
      totalBrokers: 0,
      activeBrokers: 0,
      inactiveBrokers: 0,
      byBrokerage: [],
      byPermission: [],
      prospects: { total: 0, active: 0, binded: 0 }
    }
  });

  // Cache unfiltered brokers for "Broker Activity By Month" chart
  // This ensures the activity chart always shows 12 months regardless of date filter
  const [unfilteredBrokers, setUnfilteredBrokers] = useState([]);

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      // Datalabels plugin configuration
      datalabels: {
        // The formatter function determines what is displayed
        formatter: (value, ctx) => {
          // 1. Get the total of all datasets
          const total = ctx.chart.data.datasets[0].data.reduce((sum, data) => sum + data, 0);
          // 2. Calculate the percentage
          const percentage = ((value / total) * 100).toFixed(1) + '%';
          // 3. Don't show a label for slices that are 0
          return value > 0 ? percentage : '';
        },
        color: '#fff', // Label color
        font: {
          weight: 'bold',
          size: 14,
        },
        // Add a shadow for better readability
        textShadow: {
          strokeWidth: 2,
          strokeColor: 'rgba(0,0,0,0.5)',
          color: 'rgba(0,0,0,0.5)',
        }
      },
      // Optional: Enhance the default tooltip as well
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw;
            const total = context.chart.getDatasetMeta(0).total;
            const percentage = ((value / total) * 100).toFixed(1) + '%';
            return `${label}: ${value} (${percentage})`;
          }
        }
      }
    },
  };
  const [chartData, setChartData] = useState({
    brokerStatusChartData: { labels: [], datasets: [] },
    brokersByBrokerageChartData: { labels: [], datasets: [] },
    brokerPermissionsChartData: { labels: [], datasets: [] },
    brokerActivityChartData: { labels: [], datasets: [] },
    prospectStatusChartData: { labels: [], datasets: [] }
  });

  const loadBrokers = async (filters = {}) => {
    const serviceUrl = process.env.REACT_APP_BASICSERVICE;
    let url = `${serviceUrl}/api/brokers/getAllBrokers`;
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.brokerage && filters.brokerage !== 'all') {
        params.append('brokerage', filters.brokerage);
    }
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
    
    const response = await fetch(url, {
      method: "GET",
      headers: { 
        "Content-Type": "application/json",
        "x-auth-token": localStorage.getItem("jwt") 
      },
    });
  
    if (!response.ok) {
      throw new Error(`Failed to fetch brokers: ${response.statusText}`);
    }
    return response.json();
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const data = await loadBrokers();
        setBrokersData(data);
        // Cache the unfiltered brokers list for "Broker Activity By Month" chart
        // This ensures the activity chart always shows the full 12-month history
        setUnfilteredBrokers(data.brokers || []);
        // This populates the brokerage filter dropdown ONCE on initial load.
        // It's important this is populated with the full, unfiltered list.
        if (data?.stats?.byBrokerage) {
          setBrokerageList(data.stats.byBrokerage);
        }
        prepareChartData(data, data.brokers);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchInitialData();
  }, []); // Empty dependency array means this runs only once.

  const handleApplyFilters = async () => {
    try {
      const filters = { startDate, endDate, brokerage: selectedBrokerage };
      // Fetches data again from the backend with the selected filters
      const filteredData = await loadBrokers(filters);
      // Updates the entire dashboard with the new, filtered data
      setBrokersData(filteredData);

      // DO NOT update unfilteredBrokers - it stays cached from initial load
      // This ensures "Broker Activity By Month" always shows the full 12-month history
      // All other charts will use the filtered data
      prepareChartData(filteredData, unfilteredBrokers);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    }
  };

  // NEW AND IMPROVED FUNCTION
const processBrokerActivityByMonth = (brokers, stats) => {
  const monthLabels = [];
  const counts = Array(12).fill(0); // An array to hold counts for the last 12 months
  const now = new Date();

  // 1. Generate labels for the last 12 months (e.g., 'Sep 23', 'Oct 23', ... 'Aug 24')
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthLabels.push(d.toLocaleString('default', { month: 'short', year: '2-digit' }));
  }

  // 2. Use the monthly activity data from backend (total transactions per month)
  if (stats?.monthlyActivity) {
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = d.getMonth() + 1; // MongoDB months are 1-based
      const key = `${year}-${month}`;
      counts[11 - i] = stats.monthlyActivity[key] || 0;
    }
  }

  return { labels: monthLabels, data: counts };
};

  const prepareChartData = (data, brokersForActivity = null) => {
    if (!data || !data.brokers || !data.stats) return;
    const { brokers, stats } = data;

    // Use unfiltered brokers for activity chart if provided, otherwise use filtered brokers
    // This ensures "Broker Activity By Month" always shows the full 12-month history
    const brokersToUseForActivity = brokersForActivity && brokersForActivity.length > 0
      ? brokersForActivity
      : brokers;

    const { labels: activityLabels, data: activityData } = processBrokerActivityByMonth(brokersToUseForActivity, stats);

    setChartData({
      brokerStatusChartData: {
        labels: ['Active', 'Inactive'],
        datasets: [{ data: [stats.activeBrokers, stats.inactiveBrokers], backgroundColor: ['#4BC0C0', '#FF6384'] }],
      },
      brokersByBrokerageChartData: {
        labels: stats.byBrokerage.map(item => item.brokerageCode),
        datasets: [
          { label: 'Total Brokers', data: stats.byBrokerage.map(item => item.total), backgroundColor: '#36A2EB' },
          { label: 'Active Brokers', data: stats.byBrokerage.map(item => item.active), backgroundColor: '#4BC0C0' }
        ],
      },
      brokerPermissionsChartData: {
        labels: stats.byPermission.map(item => item.permission),
        datasets: [{ label: 'Brokers with Permission', data: stats.byPermission.map(item => item.count), backgroundColor: '#FFCD56' }],
      },
      brokerActivityChartData: {
        labels: activityLabels,
        datasets: [{
          label: 'Active Brokers (w/ Transactions)', data: activityData, borderColor: '#FF9F40',
          backgroundColor: 'rgba(255, 159, 64, 0.2)', borderWidth: 2, tension: 0.4, fill: true,
        }],
      },
      prospectStatusChartData: {
        labels: ['Active', 'Bound', 'Other'],
        datasets: [{
          data: [ stats.prospects.active, stats.prospects.binded, stats.prospects.total - (stats.prospects.active + stats.prospects.binded)],
          backgroundColor: ['#36A2EB', '#4BC0C0', '#FFCD56'],
        }],
      }
    });
  };

  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: 'top' }, title: { display: false } },
  };

  return (
    <div className="tab-content">
      <div className="filter-bar">
        <div className="filter-group">
          <label htmlFor="start-date">Start Date:</label>
          <input id="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="filter-group">
          <label htmlFor="end-date">End Date:</label>
          <input id="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div className="filter-group">
          <label htmlFor="brokerage-filter">Brokerage:</label>
          <select id="brokerage-filter" value={selectedBrokerage} onChange={(e) => setSelectedBrokerage(e.target.value)}>
            <option value="all">All Brokerages</option>
            {brokerageList.map((item) => (
              <option key={item.brokerageCode} value={item.brokerageCode}>{item.brokerageCode}</option>
            ))}
          </select>
        </div>
        <button onClick={handleApplyFilters} className="filter-button">Apply Filters</button>
      </div>

      <div className="stats-overview">
        <div className="stat-card"><h3>Total Brokers</h3><p className="stat-value">{brokersData.stats.totalBrokers}</p></div>
        <div className="stat-card"><h3>Active Brokers</h3><p className="stat-value">{brokersData.stats.activeBrokers}</p></div>
        <div className="stat-card"><h3>Inactive Brokers</h3><p className="stat-value">{brokersData.stats.inactiveBrokers}</p></div>
        <div className="stat-card"><h3>Total Prospects</h3><p className="stat-value">{brokersData.stats.prospects?.total || 0}</p></div>
      </div>
      
      <div className="charts-row">
        <div className="chart-container"><h3>Broker Status</h3><div className="chart-wrapper"><Pie data={chartData.brokerStatusChartData} options={pieChartOptions} /></div></div>
        <div className="chart-container"><h3>Brokers By Brokerage</h3><div className="chart-wrapper"><Bar data={chartData.brokersByBrokerageChartData} options={chartOptions} /></div></div>
      </div>
      
      <div className="charts-row">
        <div className="chart-container"><h3>Broker Permissions</h3><div className="chart-wrapper"><Bar data={chartData.brokerPermissionsChartData} options={chartOptions} /></div></div>
        <div className="chart-container"><h3>Broker Activity By Month</h3><div className="chart-wrapper"><Line data={chartData.brokerActivityChartData} options={chartOptions} /></div></div>
      </div>
      
      <div className="charts-row">
        <div className="chart-container"><h3>Prospect Status Distribution</h3><div className="chart-wrapper"><Pie data={chartData.prospectStatusChartData} options={pieChartOptions} /></div></div>
        <div className="chart-container"><h3>Broker Performance</h3><div className="chart-wrapper"><Bar data={{
          labels: brokersData.brokers.slice(0, 10).map(b => `${b.firstname} ${b.lastname}`.substring(0, 15)),
          datasets: [
            { label: 'Active Prospects', data: brokersData.brokers.slice(0, 10).map(b => b.prospects?.active || 0), backgroundColor: '#36A2EB' },
            { label: 'Bound Prospects', data: brokersData.brokers.slice(0, 10).map(b => b.prospects?.binded || 0), backgroundColor: '#4BC0C0' }
          ]
        }} options={chartOptions} /></div></div>
      </div>
      
      <div className="table-container">
        <h3>Brokers Overview</h3>
        <table className="data-table">
          <thead><tr><th>Name</th><th>Email</th><th>Brokerage</th><th>Status</th><th>Prospects</th><th>Reminders</th><th>Last Activity</th></tr></thead>
          <tbody>
            {brokersData.brokers.map((broker) => (
              <tr key={broker._id}>
                <td>{`${broker.firstname} ${broker.lastname}`}</td>
                <td>{broker.email || 'N/A'}</td>
                <td>{broker.brokeragecode}</td>
                <td><span className={`status-badge ${broker.isactive ? 'active' : 'inactive'}`}>{broker.isactive ? 'Active' : 'Inactive'}</span></td>
                <td>{broker.prospects?.total || 0}
                  {broker.prospects?.active > 0 && (<span className="badge-info active-badge">{broker.prospects.active} active</span>)}
                  {broker.prospects?.binded > 0 && (<span className="badge-info binded-badge">{broker.prospects.binded} Bound</span>)}
                </td>
                <td>{broker.reminders?.total || 0}
                  {broker.reminders?.upcoming > 0 && (<span className="badge-info upcoming-badge">{broker.reminders.upcoming} upcoming</span>)}
                </td>
                <td>{broker.transactions?.lastActivity ? new Date(broker.transactions.lastActivity).toLocaleDateString() : broker.updatedAt ? new Date(broker.updatedAt).toLocaleDateString() : 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BrokersTab;